// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {injectIntl, type IntlShape} from 'react-intl';
import {pick} from 'lodash';

import SearchForm from '@homeaway/react-search-form';
import {
    syncPets,
    SYNC_STRATEGY_PET_INCLUDED
} from '@homeaway/search-url-parser';

import dateRangePickerActions from '../../actions/dateRangePicker';
import {GUEST_MODAL, DATE_MODAL} from '../../actions/modals';
import routerActions from '../../actions/router';
import {SEARCH_PARAMS_TO_CLEAR} from '../../constants/router';
import searchParamsActions from '../../actions/searchParams';
import type {State as ReduxState} from '../../reducers';
import {type SearchParamsType} from '../../reducers/searchParams';
import {isDifferent} from '../../utils';
import type {Breakpoint} from '../../utils/breakpoints';
import {isFilterSelected, parseFilterName} from '../../utils/filters';
import {
    getOptions,
    getSettings,
    getStores,
    getTranslations
} from './utils/search-form';

import './SearchFormContainer.less';

type DatesValues = {
    startDate: string,
    endDate: string
};

type GuestsValues = {
    adultsCount: number,
    childrenCount: number,
    petIncluded: boolean
};

type DispatchProps = {
    openDateRangePicker: () => void,
    closeDateRangePicker: () => void,
    openGuestPicker: () => void,
    closeGuestPicker: () => void,
    search: typeof routerActions.search,
    locationSearch: typeof routerActions.locationSearch,
    setDateRangePickerDates: typeof dateRangePickerActions.setDateRangePickerDates,
    setFilterFromTypeahead: typeof searchParamsActions.setFilterFromTypeahead
};

type StateProps = {
    abacus: any,
    adultsCount: number,
    arrival: string,
    breakpoint: Breakpoint,
    childrenCount: number,
    dateRangePickerIsOpen: boolean,
    departure: string,
    filter: string[],
    guestPickerIsOpen: boolean,
    petIncluded: boolean,
    pinnedPropertyListing: string,
    q: string,
    transDates: any,
    system: {
        fullLocale: string,
        hostname: string,
        siteName: string
    }
};

type Props = StateProps &
    DispatchProps & {
        intl: IntlShape
    };

export const searchFieldParameters = [
    'q',
    'arrival',
    'departure',
    'adultsCount',
    'childrenCount',
    'petIncluded',
    'pinnedPropertyListing',
    'filter'
];

type SelectionData = {
    destination: string,
    destinationChanged: boolean,
    subDestination: any,
    subDestinationChanged: boolean,
    destinationUuid: string,
    destinationType: string,
    listingProps: ?Object
};

export type State = {
    +q?: string,
    +filter?: string[],
    +arrival?: string,
    +departure?: string,
    +adultsCount?: number,
    +childrenCount?: number,
    +petIncluded?: boolean,
    +pinnedPropertyListing?: string | null
};

export type ComponentState = {
    q: string,
    filter: string[],
    arrival: string,
    departure: string,
    adultsCount: number,
    childrenCount: number,
    petIncluded: boolean,
    pinnedPropertyListing: string | null,
    filterSelected: boolean
};

export class SearchFormContainer extends Component<Props, ComponentState> {
    static defaultProps = {};

    constructor(props: Props) {
        super(props);

        this.state = {
            ...pick(props, searchFieldParameters)
        };

        props.setDateRangePickerDates(
            this.useDefaultDateWhenNonExist(props.arrival),
            this.useDefaultDateWhenNonExist(props.departure)
        );
    }

    componentWillReceiveProps(nextProps: Props) {
        /**
         * We need to reset the initialValues when they change for the back button
         * to work
         */
        if (
            isDifferent(
                pick(nextProps, searchFieldParameters),
                pick(this.props, searchFieldParameters)
            )
        ) {
            this.setState({...pick(nextProps, searchFieldParameters)});
        }
    }

    useDefaultDateWhenNonExist = (date: ?string): string => {
        if (date !== null && typeof date !== 'undefined') {
            return date;
        }
        return '';
    };

    getSearchParams = (newState: State) => {
        let searchParams: SearchParamsType = {
            ...pick(this.state, searchFieldParameters),
            ...newState
        };
        Object.entries(searchParams).forEach(([param, value]) => {
            if (value === '' || value === 0) {
                searchParams[param] = null;
            }
        });
        return searchParams;
    };

    handleSearchSelected = (selectionData: SelectionData) => {
        const {filter} = this.props;

        let typeaheadFilter = null;

        if (selectionData.subDestination) {
            typeaheadFilter = parseFilterName(
                selectionData.subDestination.filter.id
            );
        }

        let newParams = {
            q: selectionData.destination,
            pinnedPropertyListing: null
        };

        if (typeaheadFilter && !isFilterSelected(filter, typeaheadFilter)) {
            this.props.setFilterFromTypeahead(typeaheadFilter);
            newParams = {
                ...newParams,
                filterSelected: true
            };
        }

        this.setState(newParams);

        const onLocationChange = this.props.locationSearch || this.props.search;

        const finalParams = this.getSearchParams(newParams);

        // without removal, values of these fields will override all other values of search params
        delete finalParams.filter;
        delete finalParams.petIncluded;

        onLocationChange(finalParams);
    };

    handleDatesLatestValuesChange = (datesValues: DatesValues) => {
        this.setState({
            arrival: datesValues.startDate,
            departure: datesValues.endDate
        });
    };

    handleDatesCurrentValuesChange = (datesValues: DatesValues) => {
        const {startDate, endDate} = datesValues;
        const {setDateRangePickerDates} = this.props;

        setDateRangePickerDates(startDate, endDate);

        const finalParams = this.getSearchParams({
            arrival: startDate,
            departure: endDate
        });

        this.props.search(
            syncPets(finalParams, SYNC_STRATEGY_PET_INCLUDED),
            SEARCH_PARAMS_TO_CLEAR
        );
    };

    handleGuestsLatestValuesChange = (guestsValues: GuestsValues) => {
        this.setState({
            adultsCount: guestsValues.adultsCount,
            childrenCount: guestsValues.childrenCount,
            petIncluded: guestsValues.petIncluded
        });
    };

    handleGuestsCurrentValuesChange = (guestsValues: GuestsValues) => {
        const finalParams = this.getSearchParams({
            adultsCount: guestsValues.adultsCount,
            childrenCount: guestsValues.childrenCount,
            petIncluded: guestsValues.petIncluded
        });

        this.props.search(syncPets(finalParams, SYNC_STRATEGY_PET_INCLUDED));
    };

    handleDatesOpen = () => {
        this.props.openDateRangePicker();
    };

    handleDatesClose = () => {
        const finalParams = this.getSearchParams({
            arrival: this.state.arrival,
            departure: this.state.departure
        });
        this.props.search(syncPets(finalParams, SYNC_STRATEGY_PET_INCLUDED));

        this.props.closeDateRangePicker();
    };

    handleGuestsOpen = () => {
        this.props.openGuestPicker();
    };

    handleGuestsClose = () => {
        this.props.closeGuestPicker();
    };

    render() {
        const {
            abacus,
            intl,
            system,
            breakpoint,
            q: destination,
            arrival: startDate,
            departure: endDate,
            adultsCount,
            childrenCount,
            petIncluded,
            dateRangePickerIsOpen,
            guestPickerIsOpen
        } = this.props;

        const {guestsTranslations, datesTranslations} = getTranslations(
            this.state,
            intl
        );

        const {
            systemOptions,
            formOptions,
            destinationOptions,
            datesOptions,
            guestsOptions
        } = getOptions(system, abacus);

        const {mediaSettings} = getSettings(breakpoint);

        const {abacusStore} = getStores(abacus);

        return (
            <div className="SearchFormContainer">
                <SearchForm
                    systemOptions={systemOptions}
                    mediaSettings={mediaSettings}
                    abacusStore={abacusStore}
                    formOptions={formOptions}
                    destinationEvents={{
                        onSearchSelected: this.handleSearchSelected
                    }}
                    destinationOptions={destinationOptions}
                    destinationValues={{
                        destination
                    }}
                    datesEvents={{
                        onCurrentValuesChange: this
                            .handleDatesCurrentValuesChange,
                        onLatestValuesChange: this
                            .handleDatesLatestValuesChange,
                        onOpen: this.handleDatesOpen,
                        onClose: this.handleDatesClose
                    }}
                    datesOptions={datesOptions}
                    datesSettings={{
                        isOpen: dateRangePickerIsOpen
                    }}
                    datesTranslations={datesTranslations}
                    datesValues={{
                        startDate,
                        endDate
                    }}
                    guestsEvents={{
                        onCurrentValuesChange: this
                            .handleGuestsCurrentValuesChange,
                        onLatestValuesChange: this
                            .handleGuestsLatestValuesChange,
                        onOpen: this.handleGuestsOpen,
                        onClose: this.handleGuestsClose,
                        onExit: this.handleGuestsClose
                    }}
                    guestsSettings={{
                        isOpen: guestPickerIsOpen
                    }}
                    guestsOptions={guestsOptions}
                    guestsTranslations={guestsTranslations}
                    guestsValues={{
                        adultsCount,
                        childrenCount,
                        petIncluded
                    }}
                />
            </div>
        );
    }
}

const mapStateToProps = ({
    abacus,
    dateRangePicker,
    modals,
    searchParams: {params},
    typeaheadSuggestion: {term: typeaheadSuggestion},
    system: {fullLocale, hostname, siteName},
    viewport: {breakpoint}
}: ReduxState): StateProps => {
    return {
        abacus,
        breakpoint,
        dateRangePickerIsOpen: modals.DATES.isOpen,
        guestPickerIsOpen: modals.GUESTS.isOpen,
        transDates: dateRangePicker,
        system: {
            fullLocale,
            hostname,
            siteName
        },
        ...pick(params, searchFieldParameters),
        q: typeaheadSuggestion || ''
    };
};

const mapDispatchToProps = {
    openDateRangePicker: () => routerActions.pushModal(DATE_MODAL),
    closeDateRangePicker: () => routerActions.popModal(DATE_MODAL),
    openGuestPicker: () => routerActions.pushModal(GUEST_MODAL),
    closeGuestPicker: () => routerActions.popModal(GUEST_MODAL),
    search: routerActions.search,
    locationSearch: routerActions.locationSearch,
    setDateRangePickerDates: dateRangePickerActions.setDateRangePickerDates,
    setFilterFromTypeahead: searchParamsActions.setFilterFromTypeahead
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(SearchFormContainer);
